import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/auth.store';
import { timeConfigApi } from '@/features/time-tracking/api/time-tracking.api';
import { SympheniaLogo } from '@/components/SympheniaLogo';
import { InstallBanner } from '@/components/InstallBanner';
import { PopupBlockedDialog } from '@/components/PopupBlockedDialog';
import { primePdfDelivery } from '@/lib/export-delivery';
import { UpdateBanner } from '@/components/UpdateBanner';
import { PosteingangHinweis, PosteingangSymbol, usePosteingangZaehler } from '@/components/eingang/PosteingangSymbol';
import { AssistentLeiste, AssistentSymbol, useAssistentTaste } from '@/components/assistent/AssistentLeiste';
import { useAssistent } from '@/lib/assistent/assistent.store';
import { TenantBrandingSync } from '@/features/tenants/components/TenantBrandingSync';
import { FirmenAuswahl } from '@/components/layout/FirmenAuswahl';
import { ZweiFaktorHinweis } from '@/components/zwei-faktor/ZweiFaktorHinweis';
import { useHilfe } from '@/lib/hilfe';
import { useAdressAblageSchutz } from '@/lib/adress-ablage';
import { VertragsHinweis, VertragsSperre } from '@/components/vertraege/Vertraege';
import { SpeicherHinweis } from '@/components/speicher/SpeicherHinweis';
import { useVertraege, VERTRAGSSPERRE_EREIGNIS } from '@/lib/vertraege';
import {
  NavGroup,
  SingleNavItem,
  isVisible,
  onNavEnter,
  onNavLeave,
  seitenTitel,
  useNavLabel,
  NAV_CTX_DEFAULT,
  type NavItem,
  type NavVisibilityCtx,
} from './navigation';
import { useFreigabenZaehler } from './FreigabenZahl';
import { erfuelltRecht } from '@symphenia/shared-types';
import {
  LayoutDashboard,
  HardHat,
  Users,
  FolderKanban,
  Calculator,
  Copy,
  Mail,
  Package,
  Clock,
  FileText,
  FileCheck,
  BarChart3,
  Settings,
  UserCircle,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  ListChecks,
  FileBadge,
  ShoppingCart,
  Receipt,
  Paintbrush,
  Plug,
  ScrollText,
  Key,
  KeyRound,
  Bot,
  HardDrive,
  Route,
  Scale,
  Sliders,
  List,
  CalendarDays,
  CalendarRange,
  FileDown,
  FileOutput,
  PieChart,
  ClipboardList,
  CalendarOff,
  Smartphone,
  Wallet,
  UserCog,
  Radio,
  ShieldCheck,
  Building2,
  Package as PackageIcon,
  Euro,
  Boxes,
  KanbanSquare,
  PackageSearch,
  Inbox,
  QrCode,
  ScanLine,
  LifeBuoy,
  FileSignature,
  BookOpen,
  AlertTriangle,
} from 'lucide-react';

// ─── Top-level module navigation ──────────────────────────────────
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'nav.dashboard', icon: LayoutDashboard },
  // AP-39 12.1: oberste Ebene, unter „Dashboard" — mit Zähler und als Ablagefläche.
  { to: '/inbox', label: 'nav.inbox', icon: Inbox, requiredModule: 'INBOX.CORE', permission: 'inbox.item.read', posteingang: true },
  // AP-18 5.2: Hauptmenüpunkt ohne Untermenü — die eigenen Gespräche und Platz für längere.
  { to: '/assistant', label: 'nav.assistant', icon: Bot, requiredModule: 'AI_ASSISTANT', permission: 'ai.assistant.use' },
  // AP-29-D: Der Eintrag zeigte auf /addresses — eine Route, die es nie gab; der
  // Klick lief in die Catch-all-Umleitung zurueck aufs Dashboard.
  // AP-31 Nachtrag: Der Menüpunkt heißt wie die Seite — „Geschäftspartner", nicht „Adressen".
  { to: '/clients', label: 'nav.clients', icon: Users, requiredModule: 'CRM', adressAblage: true },
  // AP-28: Gruppe ohne Recht — sie erscheint, wenn ein Kind erscheint.
  // AP-40: `to` ist der Pfad des Bereichs-Dashboards; der Name öffnet es.
  {
    to: '/articles/dashboard',
    label: 'nav.articles',
    icon: Boxes,
    requiredModule: 'ARTICLES',
    dashboard: true,
    children: [
      { to: '/articles', label: 'nav.articlesList', icon: Package, permission: 'articles.article.read' },
      { to: '/articles/stammlisten', label: 'nav.articleLists', icon: List, permission: 'articles.article.read' },
      { to: '/articles/einstellungen', label: 'nav.articleSettings', icon: Settings, permission: 'articles.settings.manage' },
    ],
  },
  {
    to: '/project-management',
    label: 'nav.projectManagement',
    icon: FolderKanban,
    requiredModule: 'PROJECT_MGMT',
    // AP-35 Phase D: Das Modul-Dashboard ist die Startseite des Moduls. Seit AP-40 öffnet es
    // der Klick auf den Hauptmenüpunkt; den Unterpunkt „Dashboard" gibt es nicht mehr.
    dashboard: true,
    children: [
      // AP-35 Nachtrag 5: „Projekte" = Detailblatt (leer bis gewählt), daneben die Liste.
      { to: '/projects', label: 'nav.projects', icon: FolderKanban },
      { to: '/projects/list', label: 'nav.projectList', icon: List },
      // AP-35 Phase D2: Arbeitsschritt-Cockpits, zwischen Projekte und Einkauf (Blatt 11 11.).
      { to: '/work-cockpit', label: 'nav.workCockpit', icon: KanbanSquare, permission: 'projects.project.read' },
      // Aufgaben tragen bewusst kein Recht: „Meine" gilt für jeden mit dem
      // Baustein, auch ohne Leserecht am Projekt (Blatt 13 4.3).
      { to: '/project-tasks', label: 'nav.projectTasks', icon: ListChecks },
      // AP-35 Phase F: Zeugnisse gehören dem Mandanten, nicht einem Projekt (Blatt 5 7.3).
      { to: '/material-certificates', label: 'nav.materialCertificates', icon: FileBadge, permission: 'projects.project.read' },
      // AP-35 Phase G: das Einkaufs-Cockpit — nur mit dem Baustein Beschaffung (Blatt 5 5.3).
      { to: '/purchasing', label: 'nav.purchasing', icon: ShoppingCart, permission: 'projects.bom.manage', requiredModule: 'PROJECT_MGMT.PROCUREMENT' },
      { to: '/project-billing', label: 'nav.projectBilling', icon: Receipt, permission: 'projects.billing.run', requiredModule: 'PROJECT_MGMT.BILLING' },
      // AP-35 Phase C: Vorgaben, Nummernkreis, Phasen, Qualifikationen.
      { to: '/projects/settings', label: 'nav.projectSettings', icon: Settings, permission: 'projects.settings.manage' },
    ],
  },
  // AP-37: Kalkulation — ein eigener Bereich zwischen Projektmanagement und
  // Belegwesen. Gruppe ohne Recht: Sie erscheint, wenn ein Kind erscheint.
  {
    to: '/calculations/dashboard',
    label: 'nav.calculation',
    icon: Calculator,
    requiredModule: 'CALCULATION',
    dashboard: true,
    children: [
      { to: '/calculations', label: 'nav.calculations', icon: Calculator, permission: 'calculation.calculation.read' },
      // Detailblatt O 9.1: Cockpit „Materialanfragen" zwischen Kalkulationen und Vorlagen.
      { to: '/calculations/material-requests', label: 'nav.calculationMaterialRequests', icon: PackageSearch, permission: 'calculation.request.manage' },
      { to: '/calculations/templates', label: 'nav.calculationTemplates', icon: Copy, permission: 'calculation.calculation.read' },
      { to: '/calculations/settings', label: 'nav.calculationSettings', icon: Settings, permission: 'calculation.settings.manage' },
    ],
  },
  // AP-32: Gruppe ohne Recht — sie erscheint, wenn ein Kind erscheint.
  {
    to: '/correspondence/dashboard',
    label: 'nav.correspondence',
    icon: Mail,
    requiredModule: 'CORRESPONDENCE',
    dashboard: true,
    children: [
      { to: '/correspondence', label: 'documents.nav.list', icon: FileText, permission: 'correspondence.document.read' },
      { to: '/correspondence/dunning', label: 'documents.nav.dunning', icon: Receipt, permission: 'correspondence.dunning.create' },
      { to: '/correspondence/settings', label: 'documents.nav.settings', icon: Settings, permission: 'correspondence.settings.manage' },
    ],
  },
  // AP-34 (Nachtrag 2, A9): eigener Hauptmenüpunkt direkt unter „Schriftverkehr" — nicht dessen
  // Unterpunkte, denn das Eingangsmodul läuft ohne Belegwesen. Der Name öffnet das Dashboard (Phase N, AP-40).
  {
    to: '/incoming-invoices/dashboard',
    label: 'nav.incomingInvoices',
    icon: Receipt,
    requiredModule: 'INCOMING_INVOICES',
    dashboard: true,
    children: [
      { to: '/incoming-invoices', label: 'nav.incomingInvoicesList', icon: FileText, permission: 'incoming_invoices.invoice.read' },
      { to: '/incoming-invoices/approvals', label: 'nav.incomingApprovals', icon: ListChecks, permission: 'incoming_invoices.approval.decide', freigaben: true },
      { to: '/incoming-invoices/inbox', label: 'nav.incomingInbox', icon: Inbox, permission: 'incoming_invoices.invoice.create' },
      { to: '/incoming-invoices/open-items', label: 'nav.incomingOpenItems', icon: Wallet, permission: 'incoming_invoices.invoice.read' },
      { to: '/incoming-invoices/payment-runs', label: 'nav.incomingPaymentRuns', icon: Euro, permission: 'incoming_invoices.payment.manage' },
      { to: '/incoming-invoices/overhead', label: 'nav.incomingOverhead', icon: PieChart, permission: 'incoming_invoices.invoice.read.all' },
      { to: '/incoming-invoices/handover', label: 'nav.incomingHandover', icon: FileOutput, permission: 'incoming_invoices.export.create' },
      { to: '/incoming-invoices/settings', label: 'nav.incomingSettings', icon: Settings, permission: 'incoming_invoices.settings.manage' },
    ],
  },
  // AP-46 11.2 (BD-43): eigener Hauptmenüpunkt; der Name öffnet das Dashboard (AP-40).
  {
    to: '/site-docs/dashboard',
    label: 'nav.siteDocs',
    icon: HardHat,
    requiredModule: 'SITE_DOCS',
    dashboard: true,
    children: [
      { to: '/site-docs', label: 'nav.siteDocsList', icon: FileText, permission: 'sitedocs.document.read' },
      // AP-47 6.2: Mängel aus den Montageprotokollen.
      { to: '/site-docs/defects', label: 'nav.siteDocsDefects', icon: ListChecks, permission: 'sitedocs.document.read', requiredModule: 'SITE_DOCS.ASSEMBLY' },
      // AP-48 7.: Bautagebuch mit Projektwahl und Behinderungsliste — nur mit dem Baustein.
      { to: '/site-docs/diary', label: 'nav.siteDiary', icon: BookOpen, permission: 'sitedocs.document.read', requiredModule: 'SITE_DOCS.DIARY' },
      { to: '/site-docs/diary/hindrances', label: 'nav.siteDiaryHindrances', icon: AlertTriangle, permission: 'sitedocs.document.read', requiredModule: 'SITE_DOCS.DIARY' },
      // AP-49 8.: offene Punkte aus Besprechungen — nur mit dem Baustein Projektbesprechung.
      { to: '/site-docs/meeting/open-items', label: 'nav.siteDocsMeetingOpenItems', icon: ListChecks, requiredModule: 'SITE_DOCS.MEETING', permission: 'sitedocs.document.read' },
      { to: '/settings/site-docs', label: 'nav.siteDocsSettings', icon: Settings, permission: 'sitedocs.settings.manage' },
    ],
  },
  { to: '/inventory', label: 'nav.inventory', icon: Package, requiredModule: 'INVENTORY' },
  // Sichtbar erst, wenn PAYROLL gebucht ist (AP-21-G, Gate seit AP-29-D am Pfad).
  // AP-22-B: Stammdaten als Untereinträge. Die Gruppe trägt kein Recht — sie
  // erscheint, sobald ein Kind erscheint (AP-30-D).
  {
    to: '/payroll/dashboard',
    label: 'nav.payroll',
    icon: Wallet,
    requiredModule: 'PAYROLL',
    // AP-40 7.6: Wie die anderen Bereiche öffnet der Name das Dashboard und blendet das
    // Untermenü ein; die frühere „Übersicht" ist dieses Dashboard, ihr Unterpunkt entfällt.
    dashboard: true,
    children: [
      // AP-22 Teil 2, J: Stand je Mitarbeiter — wer erfasst, wer fertiggestellt ist.
      { to: '/payroll/months', label: 'nav.payrollMonths', icon: ListChecks, permission: 'payroll.month.read' },
      { to: '/payroll/entry', label: 'nav.payrollEntry', icon: CalendarRange, permission: 'payroll.day_entry.read' },
      { to: '/payroll/export', label: 'nav.payrollExport', icon: FileDown, permission: 'payroll.day_entry.read' },
      // AP-22 Teil 2, F: die Datei für BMD aus den fertiggestellten Monaten.
      { to: '/payroll/bmd', label: 'nav.payrollBmd', icon: FileOutput, permission: 'payroll.bmd.export' },
      { to: '/payroll/work-schedules', label: 'nav.payrollWorkSchedules', icon: ClipboardList, permission: 'payroll.work_schedule.read' },
      // AP-22 Teil 3: Kollektivverträge und Pauschale — zugeordnet wird am Arbeitszeitmodell.
      { to: '/payroll/compensation', label: 'nav.payrollCompensation', icon: Scale, permission: 'payroll.collective_agreement.read' },
      { to: '/payroll/holidays', label: 'nav.payrollHolidays', icon: CalendarDays, permission: 'payroll.holiday.read' },
      { to: '/payroll/absence-types', label: 'nav.payrollAbsenceTypes', icon: CalendarOff, permission: 'payroll.absence_type.read' },
      { to: '/payroll/settings', label: 'nav.payrollSettings', icon: Sliders, permission: 'payroll.setting.read' },
      // AP-38-F: Mitarbeiter anlegen, auch ohne Zugang (Betrieb „nur Lohnverrechnung").
      { to: '/payroll/employees', label: 'nav.payrollEmployees', icon: Users, permission: 'payroll.employee.read' },
      // AP-38-D: Die Lohnverrechnung selbst sieht diesen Eintrag nicht — sie vergibt sich nicht weiter.
      { to: '/payroll/access', label: 'nav.payrollAccess', icon: UserCog, permission: 'payroll.access.manage' },
    ],
  },
  {
    // AP-40 MD-5: Das Dashboard bekommt einen eigenen Pfad; `/time-tracking` bleibt „Buchen",
    // damit Lesezeichen und Verweise auf die Buchungsmaske gültig bleiben.
    to: '/time-tracking/dashboard',
    label: 'nav.timeTracking',
    icon: Clock,
    requiredModule: 'TIME_TRACKING',
    dashboard: true,
    children: [
      // Reihenfolge vom Auftraggeber vorgegeben (6.9.2026) — bewusst nicht nach
      // Rolle oder AP sortiert. Die Auswertungen hängen ohne eigene Untergruppe
      // direkt hier, damit sie ohne zusätzliches Aufklappen erreichbar sind;
      // READONLY sieht davon nur die Zusammenfassung, der Rest bleibt MANAGER+.
      { to: '/time-tracking/summary', label: 'nav.timeTrackingSummary', icon: BarChart3, permission: 'time.entry.read.all' },
      { to: '/time-tracking', label: 'nav.timeTrackingBooking', icon: Clock },
      // Fehlzeiten (AP-19) und eigener Monatsbericht (AP-20): alle Buchungsrollen
      { to: '/time-tracking/absences', label: 'nav.timeTrackingAbsences', icon: CalendarOff },
      { to: '/time-tracking/monthly-report', label: 'nav.timeTrackingMyMonthlyReport', icon: FileText },
      { to: '/time-tracking/current', label: 'nav.timeTrackingCurrent', icon: Radio, permission: 'time.entry.read_running' },
      { to: '/time-tracking/daily-work-times', label: 'nav.timeTrackingDailyWorkTimes', icon: Receipt, permission: 'time.daily_work_time.read' },
      { to: '/time-tracking/monthly-report-overview', label: 'nav.timeTrackingMonthlyReportOverview', icon: FileCheck, permission: 'time.report.overview' },
      // ── Einrichtung (ADMIN) ──────────────────────────────────────────
      {
        to: '/time-tracking/setup',
        label: 'nav.timeTrackingSetup',
        icon: Settings,
        children: [
          // KA-46: je Eintrag das Recht des Endpunkts dahinter und der Baustein, dem er gehört.
          { to: '/time-tracking/config', label: 'nav.timeTrackingConfig', icon: Settings, permission: 'time.config.manage' },
          { to: '/time-tracking/data-path', label: 'nav.timeTrackingDataPath', icon: Route, permission: 'time.config.manage' },
          { to: '/time-tracking/monthly-report-config', label: 'nav.timeTrackingMonthlyReport', icon: FileText, permission: 'time.report.configure' },
          { to: '/time-tracking/absence-types', label: 'nav.timeTrackingAbsenceTypes', icon: CalendarOff, permission: 'time.absence_type.manage' },
          { to: '/time-tracking/user-settings', label: 'nav.timeTrackingUserSettings', icon: Sliders, permission: 'time.user_settings.manage' },
          { to: '/time-tracking/fields', label: 'nav.timeTrackingFields', icon: ListChecks, permission: 'time.config.manage', requiredModule: 'TIME_TRACKING.CUSTOM_FIELDS' },
          { to: '/time-tracking/lists', label: 'nav.timeTrackingLists', icon: List, permission: 'time.selection.manage' },
          { to: '/time-tracking/qr-tags', label: 'nav.timeTrackingQrTags', icon: QrCode, permission: 'time.qr_tag.read', requiredModule: 'TIME_TRACKING.SCAN' },
          { to: '/time-tracking/scan-rules', label: 'nav.timeTrackingScanRules', icon: ScanLine, permission: 'time.config.manage', requiredModule: 'TIME_TRACKING.SCAN' },
        ],
      },
    ],
  },
  // AP-35 2.: Bautagebuch, Tagesberichte und Besprechungsprotokolle standen hier
  // unter `PROJECT_MGMT` — ohne Route. Seit AP-46 sind sie Bausteine der Gruppe
  // Baustellendokumente (`SITE_DOCS.DIARY`, `SITE_DOCS.MEETING`; Tagesberichte gehen im
  // Bautagebuch auf) und erscheinen unter dem eigenen Menüpunkt „Baustellendokumente".
  { to: '/analytics', label: 'nav.analytics', icon: BarChart3, requiredModule: 'REPORTING' },
];

// ─── Bottom navigation ────────────────────────────────────────────
const BOTTOM_ITEMS: NavItem[] = [
  // AP-29-E: Betreiber-Konsole. Steht bewusst ohne `roles` da — die
  // Rollenhierarchie gilt innerhalb eines Mandanten; hier entscheidet allein
  // die Zugehörigkeit zum Betreiber. Kein Kunde sieht diesen Eintrag.
  //
  // Eine **Gruppe**, seit die Konsole drei Seiten hat: Nebeneinander hiess der
  // erste Eintrag „Betreiber-Konsole" und war in Wahrheit die Mandantenliste —
  // wer sie suchte, suchte hinter einem Namen, der sie nicht nennt. Die Kinder
  // tragen kein `operatorOnly`: Das Tor steht am Eltern-Eintrag, und `NavGroup`
  // rendert ohnehin nichts, was der Eltern-Filter vorher weggenommen hat.
  {
    to: '/admin',
    label: 'nav.operatorConsole',
    icon: ShieldCheck,
    operatorOnly: true,
    children: [
      { to: '/admin/mandanten', label: 'nav.operatorTenants', icon: Building2 },
      { to: '/admin/pakete', label: 'nav.operatorPackages', icon: PackageIcon },
      { to: '/admin/preise', label: 'nav.operatorPrices', icon: Euro },
      // AP-43: Modell je KI-Aufgabe für alle Mandanten ohne eigene Wahl.
      { to: '/admin/ki-vorgaben', label: 'nav.operatorAiDefaults', icon: Bot },
      // AP-38-E: Frist, Ankündigung und Zurücksetzen der Zwei-Faktor-Anmeldung.
      { to: '/admin/zwei-faktor', label: 'nav.operatorTwoFactor', icon: KeyRound },
      // AP-41: Verträge — Dokumente, Fassungen, Stand je Mandant
      { to: '/admin/vertraege', label: 'nav.operatorContracts', icon: FileSignature },
    ],
  },
  {
    // AP-29-N-D: Die REST-Schnittstelle ist seit der Zerlegung ein verkaufter
    // Baustein. Vorher war sie offen — ein PRO-Kunde benutzte damit den
    // PRO+-Zugriff, ohne dafür zu zahlen (Befund AP-29-G).
    to: '/integration',
    label: 'nav.integration',
    icon: Plug,
    requiredModule: 'INTEGRATION_API',
    children: [
      { to: '/integration/api-keys', label: 'nav.integrationApiKeys', icon: Key, permission: 'integration.api_key.read' },
      { to: '/integration/sync-log', label: 'nav.integrationSyncLog', icon: ScrollText, permission: 'integration.api_key.read' },
    ],
  },
  {
    to: '/settings',
    label: 'nav.settings',
    icon: Settings,
    children: [
      { to: '/users', label: 'nav.users', icon: Users, permission: 'core.user.read' },
      // AP-30-E: Rollen und Rechte des Mandanten
      { to: '/settings/roles', label: 'nav.roles', icon: ShieldCheck, permission: 'core.role.manage' },
      { to: '/mitarbeiter-app', label: 'nav.employeeApp', icon: Smartphone, permission: 'core.user.read' },
      { to: '/settings', label: 'nav.tenantSettings', icon: Settings, permission: 'core.tenant.update' },
      { to: '/settings/branding', label: 'nav.branding', icon: Paintbrush, permission: 'core.branding.manage' },
      { to: '/settings/modules', label: 'nav.modules', icon: Package, permission: 'core.module.read' },
      { to: '/settings/ai', label: 'nav.aiSettings', icon: Bot, permission: 'ai.config.read', requiredModule: 'AI_ASSISTANT' },
      // AP-11-E4: Speicher des Betriebs — belegt, Kontingent, Aufteilung
      { to: '/settings/storage', label: 'nav.storage', icon: HardDrive, permission: 'core.storage.read' },
      // AP-41: Verträge des Betriebs (AGB, AVV, KI-Zusatz)
      { to: '/settings/vertraege', label: 'nav.contracts', icon: FileSignature, permission: 'core.contract.accept' },
    ],
  },
  // AP-18 Nachtrag 1: die Hilfe — sichtbar, sobald für den Benutzer ein Thema freigegeben ist (H-9).
  { to: '/hilfe', label: 'nav.help', icon: LifeBuoy, hilfe: true },
  { to: '/profile', label: 'nav.profile', icon: UserCircle },
];


/**
 * Lädt den gewählten Datenweg aus der Zeiterfassungs-Config und leitet daraus ab,
 * welche Einrichtungs-Menüpunkte sichtbar sein sollen.
 *
 * AP-30-D: Geladen wird nur mit `time.config.manage` — demselben Recht, das
 * `GET /time-config` verlangt. Vorher stand hier die Rollenprüfung `ADMIN ||
 * SUPER_ADMIN`, also eine **zweite** Beschreibung derselben Sperre; wer sie
 * einmal enger als den Endpunkt zog, bekam eine Navigation, die weniger zeigt,
 * als der Benutzer darf.
 *
 * Fail-open: bei Ladefehler bleiben alle Punkte sichtbar.
 */
function useTimeTrackingNav(): NavVisibilityCtx {
  const { user, hasModule } = useAuthStore();
  const canLoad =
    erfuelltRecht('time.config.manage', user?.permissions) && hasModule('TIME_TRACKING');
  const [cfg, setCfg] = useState<NavVisibilityCtx>(NAV_CTX_DEFAULT);

  useEffect(() => {
    if (!canLoad) return;
    let cancelled = false;
    timeConfigApi
      .get()
      .then(({ data }) => {
        if (cancelled) return;
        const external =
          data.projectSource === 'EXTERNAL_API' ||
          data.activityTypeSource === 'EXTERNAL_API' ||
          data.userSource === 'EXTERNAL_API';
        const fieldsExist =
          (data.timeEntryCustomFieldsSchema?.length ?? 0) + (data.customFieldsSchema?.length ?? 0) > 0;
        let storedFields: string | null = null;
        try {
          storedFields = localStorage.getItem('symphenia.tt.useCustomFields');
        } catch {
          storedFields = null;
        }
        setCfg({
          // AP-35: Mit angeschlossenem Projektmanagement wird hier nichts gepflegt.
          usesManual: data.projectSource !== 'EXTERNAL_API' && data.projectSource !== 'PROJECT_MGMT',
          usesExternal: external,
          usesQr: Boolean(data.qrRequired),
          usesFields: storedFields == null ? fieldsExist : storedFields === 'true',
        });
      })
      .catch(() => {
        // Fail-open: im Zweifel alle Einrichtungspunkte zeigen
      });
    return () => {
      cancelled = true;
    };
  }, [canLoad]);

  return cfg;
}


// ─── Main layout ──────────────────────────────────────────────────

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout, hasModule } = useAuthStore();
  // AP-30-D: Dieselbe Funktion wie im `PermissionGuard` — ein zweiter Rechenweg
  // wäre die erste Abweichung zwischen Menü und Endpunkt.
  const darf = (p: string) => erfuelltRecht(p, user?.permissions);
  const isOperator = user?.isOperator === true;
  const navCfg = useTimeTrackingNav();
  const [collapsed, setCollapsed] = useState(false);
  usePosteingangZaehler();
  // Eine Internetadresse, die neben eine Ablagefläche fällt, öffnete der Browser sonst in diesem Tab.
  useAdressAblageSchutz();
  useFreigabenZaehler();
  // AP-18 5.1: Strg + K öffnet die Seitenleiste des Assistenten; mit jeder Frage geht mit, wie
  // das Menü die Seite nennt. Gespräche gehören dem Benutzer — wechselt er oder die Firma, ist
  // die Leiste wieder leer und zu.
  useAssistentTaste();
  const navLabel = useNavLabel();
  const { pathname } = useLocation();
  const ort = seitenTitel([...NAV_ITEMS, ...BOTTOM_ITEMS], pathname, navLabel);
  const assistentFuer = user ? `${user.tenantId}:${user.id}` : '';
  useEffect(() => {
    useAssistent.getState().zuruecksetzen();
  }, [assistentFuer]);
  // AP-18 Nachtrag 1: Inhaltsverzeichnis der Hilfe einmal je Anmeldung — neu, wenn Firma, Rechte
  // oder Bausteine wechseln. Leer = kein Menüpunkt.
  const hilfeLaden = useHilfe((s) => s.laden);
  const hilfeInhalt = useHilfe((s) => s.inhalt);
  useEffect(() => {
    if (user) void hilfeLaden(`${user.tenantId}:${user.id}:${[...user.permissions].sort().join(',')}:${[...user.activeModules].sort().join(',')}`);
  }, [user, hilfeLaden]);
  const zeigeHilfe = (item: NavItem) => !item.hilfe || (hilfeInhalt?.length ?? 0) > 0;
  // AP-41: Vertragsstand einmal je Anmeldung — und sofort neu, wenn der Server sperrt.
  const vertraegeLaden = useVertraege((s) => s.laden);
  const gesperrt = useVertraege((s) => s.status?.gesperrt === true);
  useEffect(() => {
    if (!user) return;
    const fuer = `${user.tenantId}:${user.id}`;
    void vertraegeLaden(fuer);
    const neu = () => void vertraegeLaden(fuer, true);
    // Eine neue Fassung erscheint auch in einer laufenden Sitzung: bei Fokus und alle 5 Minuten.
    const uhr = window.setInterval(neu, 5 * 60_000);
    window.addEventListener(VERTRAGSSPERRE_EREIGNIS, neu);
    window.addEventListener('focus', neu);
    return () => {
      window.clearInterval(uhr);
      window.removeEventListener(VERTRAGSSPERRE_EREIGNIS, neu);
      window.removeEventListener('focus', neu);
    };
  }, [user, vertraegeLaden]);

  // Die Mandanten-Einstellung „PDF herunterladen oder im Tab öffnen" einmal
  // vorladen: `downloadFile()` liest sie synchron, wenn der Export fertig ist.
  useEffect(() => { primePdfDelivery(); }, []);

  return (
    <div className="flex h-screen bg-background">
      {/* Mandanten-Primärfarbe laden und auf das Theme legen (rendert nichts) */}
      <TenantBrandingSync />
      {/* Update-Hinweis bei neuer App-Version (Service Worker) */}
      <UpdateBanner />
      {/* Erklärt den blockierten PDF-Tab — gilt für jeden PDF-Weg der Anwendung */}
      <PopupBlockedDialog />
      {/* AP-39: Rückmeldung nach dem Ablegen im Menükopf oder am Menüpunkt */}
      <PosteingangHinweis />
      {/* Sidebar */}
      <aside
        className={`flex ${collapsed ? 'w-16' : 'w-56'} flex-shrink-0 flex-col transition-[width] duration-200`}
        style={{ backgroundColor: 'var(--sidebar-bg)', color: 'var(--sidebar-fg)' }}
      >
        {/* Logo + collapse toggle */}
        <div
          className={`flex h-14 items-center ${collapsed ? 'justify-center' : 'justify-between'} px-3`}
          style={{ borderBottom: '1px solid var(--sidebar-border)' }}
        >
          {collapsed ? (
            /* Eingeklappt: 2 Pillen + blauer Punkt */
            <button
              onClick={() => setCollapsed(false)}
              className="rounded-md p-1 transition-colors"
              title="Menü ausklappen"
            >
              <svg
                width={22}
                height={22}
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect x="10" y="3" width="27" height="15" rx="7.5" fill="var(--sidebar-fg)" />
                <rect x="3" y="22" width="27" height="15" rx="7.5" fill="#0ea5e9" />
              </svg>
            </button>
          ) : (
            <>
              <a
                href="https://www.symphenia.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-1 rounded-md"
                title="symphenia.com"
              >
                <SympheniaLogo size={20} context="sidebar" />
              </a>
              <div className="flex items-center gap-1">
              <AssistentSymbol />
              <PosteingangSymbol />
              <button
                onClick={() => setCollapsed(true)}
                className="rounded-md p-1.5 transition-colors"
                style={{ color: 'var(--sidebar-fg)' }}
                onMouseEnter={onNavEnter}
                onMouseLeave={onNavLeave}
                title="Menü einklappen"
              >
                <PanelLeftClose size={18} />
              </button>
              </div>
            </>
          )}
        </div>

        {/* AP-38-B: Firma dieses Tabs — nur sichtbar, wenn es mehrere gibt */}
        <FirmenAuswahl collapsed={collapsed} onExpand={() => setCollapsed(false)} />

        {/* Main navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {NAV_ITEMS.filter((item) => isVisible(item, darf, hasModule, navCfg, isOperator)).map((item) =>
            item.children ? (
              <NavGroup key={item.to} item={item} darf={darf} collapsed={collapsed} hasModule={hasModule} navCfg={navCfg} />
            ) : (
              <SingleNavItem key={item.to} item={item} collapsed={collapsed} />
            ),
          )}
        </nav>

        {/* Bottom navigation */}
        <div
          className="px-2 py-3 space-y-0.5"
          style={{ borderTop: '1px solid var(--sidebar-border)' }}
        >
          {BOTTOM_ITEMS.filter((item) => isVisible(item, darf, hasModule, navCfg, isOperator) && zeigeHilfe(item)).map((item) =>
            item.children ? (
              <NavGroup key={item.to} item={item} darf={darf} collapsed={collapsed} hasModule={hasModule} navCfg={navCfg} />
            ) : (
              <SingleNavItem key={item.to} item={item} collapsed={collapsed} />
            ),
          )}

          {/* Logout button */}
          <button
            onClick={logout}
            className={`flex w-full items-center ${collapsed ? 'justify-center p-2' : 'gap-3 px-3 py-2'} rounded-md text-sm transition-colors`}
            style={{ color: 'var(--sidebar-fg)' }}
            title={collapsed ? t('nav.logout') : undefined}
            onMouseEnter={onNavEnter}
            onMouseLeave={onNavLeave}
          >
            <LogOut size={18} className="flex-shrink-0" />
            {!collapsed && t('nav.logout')}
          </button>
        </div>

        {/* User info + expand hint */}
        <div
          className="px-3 py-3"
          style={{ borderTop: '1px solid var(--sidebar-border)' }}
        >
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: 'var(--sidebar-active-bg)',
                  color: 'var(--sidebar-active-fg)',
                }}
                title={
                  user?.profile?.firstName
                    ? `${user.profile.firstName} ${user.profile.lastName ?? ''}`
                    : user?.email
                }
              >
                {user?.profile?.firstName?.[0]?.toUpperCase() ?? user?.email?.[0]?.toUpperCase() ?? '?'}
              </div>
              {/* Expand-Hinweis */}
              <button
                onClick={() => setCollapsed(false)}
                className="flex items-center justify-center rounded-md p-1 transition-colors"
                style={{ color: 'var(--sidebar-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--sidebar-active-fg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--sidebar-muted)'; }}
                title="Menü ausklappen"
              >
                <PanelLeftOpen size={16} />
              </button>
            </div>
          ) : (
            <>
              <p
                className="truncate text-xs font-medium"
                style={{ color: 'var(--sidebar-fg)' }}
              >
                {user?.profile?.firstName
                  ? `${user.profile.firstName} ${user.profile.lastName ?? ''}`
                  : user?.email}
              </p>
              <p className="truncate text-xs" style={{ color: 'var(--sidebar-muted)' }}>
                {user?.email}
              </p>
            </>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-background">
        {/* Installations-Hinweis (nur wenn der Browser die Installation anbietet) */}
        <div className="px-4 pt-4 sm:px-6 empty:hidden">
          <InstallBanner />
        </div>
        {/* AP-38-E: „Richte bis … die Zwei-Faktor-Anmeldung ein" — nur, solange eine Frist läuft. */}
        <div className="px-4 pt-4 sm:px-6 empty:hidden">
          <ZweiFaktorHinweis />
        </div>
        {/* AP-41: „Bitte nimm … bis … an" — nur für die, die annehmen dürfen. */}
        <div className="px-4 pt-4 sm:px-6 empty:hidden">
          <VertragsHinweis />
        </div>
        {/* AP-11-E4: „Der Speicher ist zu 84 % belegt" — ab 80 %, nur für die, die ihn einsehen dürfen. */}
        <div className="px-4 pt-4 sm:px-6 empty:hidden">
          <SpeicherHinweis />
        </div>
        {/* AP-41: Frist versäumt → Sperrbildschirm statt der Seite (Abmelden bleibt). */}
        {gesperrt ? <VertragsSperre /> : <Outlet />}
      </main>
      {/* AP-18: Seitenleiste des Assistenten — neben der Seite, auf dem Telefon als Blatt darüber. */}
      {!gesperrt && <AssistentLeiste seitenTitel={ort} />}
    </div>
  );
}
